import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DuplicateFileIcon, EditIcon, FileIconWhite } from '@/assets/icons';
import { CapTableIcon } from '@/assets/images';
import { DataRoomClientFile } from '@/common/types';
import { createFileInstanceId, FileInstance } from '@/contexts/overview/dataroom/fileInstanceUtils';
import { useContextMenu } from '@/contexts/overview/dataroom/useContextMenu';
import { useDataRoom } from '@/contexts/overview/dataroom/utils';
import { MarveriIcon } from '@/pages/overview/common/MarveriIcon';
import { useOverview } from '@/pages/overview/common/utils';
import { ContextMenu } from '@/pages/overview/dataroom/content/common/ContextMenu';
import { DraggableDataRoomFile } from '@/pages/overview/dataroom/content/common/DraggableDataRoomFile';
import { ErrorIndicator } from '@/pages/overview/dataroom/content/common/ErrorIndicator';
import {
  type FolderPath,
  getFileCurrentPath,
  getFileFolderPaths,
  sortDataRoomFiles,
} from '@/pages/overview/dataroom/content/common/folderUtils';
import { LinkedFileIndicator } from '@/pages/overview/dataroom/content/common/LinkedFileIndicator';
import { MissingSignatureIndicator } from '@/pages/overview/dataroom/content/common/MissingSignatureIndicator';
import { RenameListItemForm } from '@/pages/overview/dataroom/content/common/RenameListItemForm';
import { trpcReact } from '@/utils/trpc';

interface DataRoomFileListProps {
  fileList: DataRoomClientFile[];
  dataRoomInteraction: boolean | undefined;
  folderPath: string;
  hasSubFolders: boolean;
}

interface CheckBoxProps {
  checkBoxFile: DataRoomClientFile;
  index: number;
}

export const DataRoomFileList = ({
  fileList,
  dataRoomInteraction,
  folderPath,
  hasSubFolders,
}: DataRoomFileListProps) => {
  const featureFlags = useFlags();
  const [hoverIndex, setHoverIndex] = useState<number>();
  const [focusedFile, setFocusedFile] = useState<DataRoomClientFile | undefined>();
  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);

  const [shiftKeyPressed, setShiftKeyPressed] = useState<boolean>(false);
  const renameInputRef = useRef<HTMLInputElement>(null);

  const {
    matter: {
      id: matterId,
      number: matterNumber,
      client: { number: clientNumber },
    },
    setSelectedReferenceFocus,
    isUserCurrentEditor,
  } = useOverview();

  const {
    selectedFile,
    setSelectedFile,
    selectedRenameFile,
    setSelectedRenameFile,
    checkedFiles,
    checkedFileInstances,
    setCheckedFileInstances,
    combinedDocumentArray,
    setCheckedFiles,
    isViewerOpen,
    setIsViewerOpen,
    setIsSpreadsheetViewerOpen,
    initialClickIndex,
    setInitialClickIndex,
    goToNextDocument,
    goToPreviousDocument,
    setRenameFileIndex,
    isFileRenameActive,
    updateRenameFormStatus,
    setIsSidePanelOpen,
    updateSignature,
    sidePanelTabIndex,
    setSidePanelTabIndex,
    foldersByPaths,
    isDualViewerOpen,
    selectedFileContext,
    setSelectedFileContext,
    selectedRenameFileContext,
    setSelectedRenameFileContext,
  } = useDataRoom();

  const { isContextMenuVisible, setIsContextMenuVisible, position, setPosition } = useContextMenu();

  const [clickTimeout, setClickTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isDoubleClick, setIsDoubleClick] = useState(false);

  // Helper functions for file instance management

  const createFileInstance = (file: DataRoomClientFile, folderPath: string): FileInstance => {
    return {
      file,
      folderPath,
      instanceId: createFileInstanceId(file.id, folderPath),
    };
  };

  const isFileInstanceChecked = (file: DataRoomClientFile, folderPath: string): boolean => {
    const instanceId = createFileInstanceId(file.id, folderPath);
    return checkedFileInstances.some((instance) => instance.instanceId === instanceId);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Shift') {
        setShiftKeyPressed(true);
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Shift') {
        setShiftKeyPressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const handleArrowKeys = useCallback(
    (event: KeyboardEvent) => {
      if (selectedFile && !isViewerOpen && !isFileRenameActive) {
        if (event.key === 'ArrowDown') {
          goToNextDocument();
        } else if (event.key === 'ArrowUp') {
          goToPreviousDocument();
        } else if (event.key === 'Enter') {
          setIsViewerOpen(true);
        }
      }
    },
    [
      goToNextDocument,
      goToPreviousDocument,
      isFileRenameActive,
      isViewerOpen,
      selectedFile,
      setIsViewerOpen,
    ],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleArrowKeys);

    return () => {
      document.removeEventListener('keydown', handleArrowKeys);
    };
  }, [handleArrowKeys]);

  useEffect(() => {
    const updateScreenWidth = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', updateScreenWidth);

    return () => window.removeEventListener('resize', updateScreenWidth);
  }, []);

  const sortedFileList = useMemo(() => {
    return [...fileList].sort(sortDataRoomFiles);
  }, [fileList]);

  const getCurrentFileIndex = useCallback(
    (currentFile: DataRoomClientFile | undefined) => {
      const currentFileIndex = combinedDocumentArray.findIndex(
        (dataRoomFile) => dataRoomFile.id === currentFile?.id,
      );
      return currentFileIndex;
    },
    [combinedDocumentArray],
  );

  const getFileIndexInCombinedArray = useCallback(
    (file: DataRoomClientFile) => {
      return combinedDocumentArray.findIndex((dataRoomFile) => dataRoomFile.name === file.name);
    },
    [combinedDocumentArray],
  );

  const handleShiftKeyPressed = (start: number, end: number) => {
    let filesToSelect: DataRoomClientFile[] = [];

    if (start > end) {
      filesToSelect = combinedDocumentArray.slice(end, start);
    } else {
      filesToSelect = combinedDocumentArray.slice(start + 1, end + 1);
    }

    const uniqueFilesToSelect = filesToSelect.filter((file) => !checkedFiles.includes(file));

    // Initialize new file instances and checked files
    const newFileInstances: FileInstance[] = [];
    const newCheckedFiles: DataRoomClientFile[] = [];

    uniqueFilesToSelect.forEach((file) => {
      const fileFolderPaths = getFileFolderPaths(file, foldersByPaths);

      fileFolderPaths.forEach((fileFolderPath: FolderPath) => {
        const instanceId = createFileInstanceId(file.id, fileFolderPath);

        const instanceExists = checkedFileInstances.some(
          (instance) => instance.instanceId === instanceId,
        );

        if (!instanceExists) {
          newFileInstances.push({
            file,
            folderPath: fileFolderPath,
            instanceId,
          });
        }
      });

      if (!checkedFiles.some((checkedFile) => checkedFile.id === file.id)) {
        newCheckedFiles.push(file);
      }
    });

    if (newFileInstances.length > 0) {
      setCheckedFileInstances([...checkedFileInstances, ...newFileInstances]);
    }

    // checkedFiles will eventually be deprecated, for now it's needed for button interaction
    if (newCheckedFiles.length > 0) {
      setCheckedFiles([...checkedFiles, ...newCheckedFiles]);
    }
  };

  const handleDefaultClick = (file: DataRoomClientFile) => {
    const instanceId = createFileInstanceId(file.id, folderPath);
    const isInstanceChecked = isFileInstanceChecked(file, folderPath);

    if (isInstanceChecked) {
      // Remove this specific instance
      setCheckedFileInstances(
        checkedFileInstances.filter((instance) => instance.instanceId !== instanceId),
      );

      // Also remove from checkedFiles if no other instances of this file are checked
      const hasOtherInstances = checkedFileInstances.some(
        (instance) => instance.file.id === file.id && instance.instanceId !== instanceId,
      );
      if (!hasOtherInstances) {
        setCheckedFiles(checkedFiles.filter((checkedFile) => checkedFile.id !== file.id));
      }
    } else {
      // Add this specific instance
      const fileInstance = createFileInstance(file, folderPath);
      setCheckedFileInstances([...checkedFileInstances, fileInstance]);

      // Add to checkedFiles if not already there (for backward compatibility)
      if (!checkedFiles.some((checkedFile) => checkedFile.id === file.id)) {
        setCheckedFiles([...checkedFiles, file]);
      }

      setInitialClickIndex(
        combinedDocumentArray.findIndex((dataRoomFile) => dataRoomFile.id === file.id),
      );
      // Use explicit context for checkbox selection
      setSelectedFile(file.name, { fileId: file.id, folderPath });
    }
  };

  const toggleCheckbox = (file: DataRoomClientFile) => {
    if (shiftKeyPressed) {
      const start = initialClickIndex;
      const end = getCurrentFileIndex(focusedFile);
      handleShiftKeyPressed(start, end);
    } else {
      handleDefaultClick(file);
    }
  };

  const CheckBox = ({ checkBoxFile }: CheckBoxProps) => {
    const isInstanceChecked = isFileInstanceChecked(checkBoxFile, folderPath);
    return (
      <div className="flex items-center">
        {hoverIndex === getFileIndexInCombinedArray(checkBoxFile) || isInstanceChecked ? (
          <input
            type="checkbox"
            checked={isInstanceChecked}
            onChange={() => toggleCheckbox(checkBoxFile)}
            className={`${!isInstanceChecked && 'hover:bg-N600'} size-[12px] cursor-pointer appearance-none rounded-[2px] border border-marveri-white  bg-contain bg-center bg-no-repeat checked:border-transparent checked:bg-[url('@/assets/images/check-icon-white.svg')] checked:filter-dark`}
            data-testid="data-room-file-checkbox"
          />
        ) : (
          <div className="size-[12px]"></div>
        )}
      </div>
    );
  };

  const fileToRename = trpcReact.dataRoom.updateDataRoomFileName.useMutation();
  const updateFileName = (file: DataRoomClientFile, newName: string) => {
    if (newName === '') {
      newName = file.displayName;
    }

    // Capture context before clearing rename state
    const contextToPreserve = selectedRenameFileContext;

    fileToRename.mutate({
      name: file.name,
      clientNumber: clientNumber,
      clientMatterNumber: matterNumber,
      newName: newName,
      clientMatterId: matterId,
    });

    // Clear rename state first
    updateRenameFormStatus(false);
    setSelectedRenameFileContext(undefined);

    // Then reselect the file with preserved context
    if (contextToPreserve) {
      setSelectedFile(file.name, {
        fileId: file.id,
        folderPath: contextToPreserve.folderPath,
      });
    }
  };

  const handleMouseEnter = useCallback(
    (index: number, file: DataRoomClientFile) => {
      if (isFileRenameActive) return;
      setHoverIndex(getFileIndexInCombinedArray(file));
      if (isContextMenuVisible) return;
      setFocusedFile(file);
    },
    [getFileIndexInCombinedArray, isContextMenuVisible, isFileRenameActive],
  );

  const handleMouseLeave = () => {
    if (isFileRenameActive) return;
    setHoverIndex(undefined);
  };

  const handleCsvFileOpen = () => {
    // Open the viewer - authentication and file fetching is handled by context
    setIsSpreadsheetViewerOpen(true);
  };

  const handleFileClick = (file: DataRoomClientFile) => {
    if (clickTimeout) {
      // Double click detected
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      setIsDoubleClick(true);

      if (isFileRenameActive) return;
      setRenameFileIndex(undefined);
      updateRenameFormStatus(false);
      setSelectedRenameFileContext(undefined);
      // Use explicit context for clicks
      setSelectedFile(file.name, { fileId: file.id, folderPath });
      setSelectedReferenceFocus('referenced');
      localStorage.setItem('current-page', `0`);
      localStorage.setItem('document-list-file-id', file.id);

      // Check if it's a spreadsheet file and handle accordingly
      if (checkFileExtenstion(file.name)) {
        handleCsvFileOpen();
      } else {
        setIsViewerOpen(true);
        setIsSidePanelOpen(false);
      }
    } else {
      // Different file - wait to see if it's a double click
      setIsDoubleClick(false);
      const timeout = setTimeout(() => {
        if (!isDoubleClick) {
          // Single click logic for new file selection
          setRenameFileIndex(undefined);
          updateRenameFormStatus(false);
          setSelectedRenameFileContext(undefined);
          // Use explicit context for clicks
          setSelectedFile(file.name, { fileId: file.id, folderPath });
          setIsSidePanelOpen(true);
          localStorage.setItem('document-list-file-id', file.id);
          localStorage.setItem('document-list-side-panel-tab', sidePanelTabIndex.toString());

          // First click - check if this is a deselection
          if (selectedFile?.id === file.id && selectedFileContext?.folderPath === folderPath) {
            // Same file instance - deselect immediately
            setRenameFileIndex(undefined);
            updateRenameFormStatus(false);
            setSelectedRenameFileContext(undefined);
            setSelectedFile('');
            setSelectedFileContext(undefined);
            setIsSidePanelOpen(false);
            // Clear localStorage to prevent useEffect from re-selecting
            localStorage.removeItem('document-list-file-id');
            localStorage.removeItem('document-list-side-panel-tab');
            return; // Don't set timeout for deselection
          }
        }
      }, 300);
      setClickTimeout(timeout);
    }
  };

  useEffect(() => {
    const savedFileId = localStorage.getItem('document-list-file-id');
    const savedSidePanelTab = localStorage.getItem('document-list-side-panel-tab');
    const foundFile = sortedFileList.find((file) => file.id === savedFileId);

    // Don't restore selection during rename operations to avoid interfering with context
    // Only run if we have a saved file ID, it's different from currently selected, and not renaming
    if (
      savedFileId &&
      foundFile &&
      savedSidePanelTab &&
      !isDualViewerOpen &&
      selectedFile?.id !== savedFileId &&
      !isFileRenameActive
    ) {
      setSelectedFile(foundFile?.name || '');
      setIsSidePanelOpen(true);
      setSidePanelTabIndex(parseInt(savedSidePanelTab));
    }
  }, [
    sortedFileList,
    setSelectedFile,
    setIsSidePanelOpen,
    setSidePanelTabIndex,
    selectedFile?.id,
    isDualViewerOpen,
    isFileRenameActive,
  ]);

  const handleRightClick = (e: React.MouseEvent, file: DataRoomClientFile) => {
    if (!dataRoomInteraction) return;
    e.preventDefault();
    setFocusedFile(file);
    setIsContextMenuVisible(true);
    setPosition({
      x: e.clientX,
      y: e.clientY,
    });
  };

  const handleMouseLeaveFileList = () => {
    setIsContextMenuVisible(false);
  };

  const handleRenameClick = (e: React.MouseEvent<Element>, file: DataRoomClientFile) => {
    e.stopPropagation();
    updateRenameFormStatus(true);
    setSelectedRenameFile(file);
    // Set the specific file+folder context for rename form display
    setSelectedRenameFileContext({ fileId: file.id, folderPath });
  };

  const handleRemoveSignature = (
    event: React.MouseEvent<Element, MouseEvent>,
    fileName: string,
  ) => {
    event?.stopPropagation();
    updateSignature(fileName, false);
  };

  const handleRenameFormClose = () => {
    // Capture context before clearing rename state
    const fileToPreserve = selectedRenameFile;
    const contextToPreserve = selectedRenameFileContext;

    // Clear rename state first
    setRenameFileIndex(undefined);
    updateRenameFormStatus(false);
    setSelectedRenameFileContext(undefined);

    // Then reselect the file with preserved context
    if (fileToPreserve && contextToPreserve) {
      setSelectedFile(fileToPreserve.name, {
        fileId: fileToPreserve.id,
        folderPath: contextToPreserve.folderPath,
      });
    }
  };

  const handleDuplicateClick = (e: React.MouseEvent<Element>, file: DataRoomClientFile) => {
    e.stopPropagation();
    setSelectedFile(file.name);
    localStorage.setItem('document-list-file-id', file.id);
    setIsSidePanelOpen(true);
    setSidePanelTabIndex(0);
    setTimeout(() => {
      const duplicateSection = document.getElementById('duplicate-files-section');
      if (duplicateSection) {
        duplicateSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const checkFileExtenstion = (filename: string) => {
    const fileExtension = filename.split('.').pop()?.toLowerCase();

    if (fileExtension === 'xlsx' || fileExtension === 'xls' || fileExtension === 'csv') {
      return true;
    }

    return false;
  };

  const getErroredOperations = useCallback((file: DataRoomClientFile) => {
    return file.operation.filter((op) => op.operationStatus === 'ERRORED');
  }, []);

  const hasErroredOperations = useCallback(
    (file: DataRoomClientFile): boolean => {
      return getErroredOperations(file).length > 0;
    },
    [getErroredOperations],
  );

  const truncateDisplayName = useCallback(
    (fileDisplayName: string) => {
      if (screenWidth <= 1440) {
        if (fileDisplayName.length <= 150) {
          return fileDisplayName;
        }
        const start = fileDisplayName.slice(0, 55);
        const end = fileDisplayName.slice(-20);
        return `${start} ... ${end}`;
      } else {
        return fileDisplayName;
      }
    },
    [screenWidth],
  );

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeout) {
        clearTimeout(clickTimeout);
      }
    };
  }, [clickTimeout]);

  if (sortedFileList.length <= 0 && !hasSubFolders) {
    return (
      <div>
        <div className="flex gap-[14px] px-[35px] py-[12px] pl-[70px]">
          <span>
            <i>(Empty)</i>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div onMouseLeave={handleMouseLeaveFileList}>
      {sortedFileList.map((file, index) => (
        <div
          id={file.name}
          key={file.name}
          className={`${isFileRenameActive && selectedRenameFileContext && selectedRenameFileContext.fileId === file.id && selectedRenameFileContext.folderPath === folderPath && 'bg-[#2e2e2e]'} ${
            isFileInstanceChecked(file, folderPath)
              ? 'bg-[#A9A9A9] text-[#121212]'
              : 'hover:bg-[#292929]'
          } ${selectedFileContext && selectedFileContext.fileId === file.id && selectedFileContext.folderPath === folderPath && 'bg-[#292929] ring-2 ring-inset ring-marveri-gold'} 
          ${index === 0 && 'border-none'} ${index === fileList.length - 1 && 'rounded-b-[4px]'} flex items-center border-t border-[#292929] pl-[30px] pr-[12px]`}
          onMouseEnter={() => handleMouseEnter(index, file)}
          onMouseLeave={handleMouseLeave}
          data-testid="data-room-file-list-item"
        >
          <CheckBox checkBoxFile={file} index={index} />
          <div className="ml-3 w-full">
            <DraggableDataRoomFile
              key={file.id}
              draggableId={createFileInstanceId(file.id, folderPath)}
              currentDroppableId={file.id}
              disabled={isFileRenameActive || !isUserCurrentEditor}
            >
              <div
                className={`relative flex h-[50px] w-full cursor-pointer items-center gap-[14px] py-[12px] text-[14px] font-medium`}
                key={file.id}
                onClick={() => handleFileClick(file)}
                onContextMenu={(e) => handleRightClick(e, file)}
              >
                <div className="flex w-full items-center gap-[12px]">
                  {checkFileExtenstion(file.name) ? (
                    <img src={CapTableIcon} alt="file-icon" className="h-[13px]" />
                  ) : (
                    <img
                      src={FileIconWhite}
                      alt="file-icon"
                      className={`${isFileInstanceChecked(file, folderPath) && 'filter-dark'} h-[13px]`}
                    />
                  )}
                  {isFileRenameActive &&
                  selectedRenameFileContext &&
                  selectedRenameFileContext.fileId === file.id &&
                  selectedRenameFileContext.folderPath === folderPath &&
                  selectedRenameFile ? (
                    <RenameListItemForm
                      onClose={handleRenameFormClose}
                      onSubmit={(newName) => updateFileName(selectedRenameFile, newName)}
                      currentName={selectedRenameFile.displayName}
                      renameInputRef={renameInputRef}
                      setRenameFileIndex={setRenameFileIndex}
                    />
                  ) : (
                    <span className="flex break-words text-left text-[14px] tracking-[0.14px]">
                      {truncateDisplayName(file.displayName)}
                    </span>
                  )}
                  {file.duplicateCluster && (
                    <div className="group relative shrink-0">
                      <img
                        src={DuplicateFileIcon}
                        alt="duplicate-file-icon"
                        className="translate-y-[2px] cursor-pointer group-hover:filter-hover-filter"
                        onClick={(e) => {
                          handleDuplicateClick(e, file);
                        }}
                      />
                      <div
                        className={`${getFileCurrentPath(file, foldersByPaths) === `/root/${file.displayName}` && index === 0 ? 'left-[45%] top-8 -translate-x-1/2 after:absolute after:bottom-full after:left-1/2 after:-translate-x-1/2 after:rotate-180 after:border-8 after:border-transparent after:border-t-white' : '-top-7 left-[45%] -translate-x-1/2 after:absolute after:left-1/2 after:top-full after:-translate-x-1/2 after:border-8 after:border-transparent after:border-t-white'} absolute z-[100] hidden w-[122px]  rounded-[8px] border border-[#D9D9D9] bg-white text-center text-[12px] font-medium text-black group-hover:block`}
                      >
                        Duplicate detected
                      </div>
                    </div>
                  )}
                  {featureFlags.linkedFiles && (
                    <LinkedFileIndicator
                      file={file}
                      foldersByPaths={foldersByPaths}
                      containerStyle="shrink-0"
                    />
                  )}
                  {hoverIndex === getFileIndexInCombinedArray(file) &&
                    !isFileRenameActive &&
                    dataRoomInteraction && (
                      <MarveriIcon
                        icon={EditIcon}
                        onClick={(event) => handleRenameClick(event, file)}
                        iconStyle="shrink-0 z-20 translate-y-[4px]"
                        iconType="list-main"
                        disabled={!isUserCurrentEditor}
                      />
                    )}

                  {hasErroredOperations(file) && (
                    <ErrorIndicator
                      fileName={file.name}
                      erroredOperations={getErroredOperations(file)}
                      containerStyle="shrink-0"
                    />
                  )}
                  {file.missingSignature === true && (
                    <MissingSignatureIndicator
                      fileName={file.name}
                      focusedFileName={selectedRenameFile?.name}
                      isRenameActive={isFileRenameActive}
                      containerStyle="ml-auto shrink-0"
                      onRemoveSignature={(event: React.MouseEvent) =>
                        handleRemoveSignature(event, file.name)
                      }
                      disabled={!isUserCurrentEditor}
                    />
                  )}
                </div>
              </div>
            </DraggableDataRoomFile>
          </div>
        </div>
      ))}
      {isContextMenuVisible && (
        <ContextMenu focusedFile={focusedFile} xPosition={position.x} yPosition={position.y} />
      )}
    </div>
  );
};
